import { SubscriptionUpdatedEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

const bonusVariantPriceCache = new Map();

/**
 * Composant d'abonnement V2 permettant de gérer plusieurs cartes d'abonnement.
 */
class SubscriptionV2Component extends HTMLElement {
  constructor() {
    super();

    this.sellingPlanInput = null;
    this.oneTimeOption = null;
    this.subscriptionOption = null;
    this.subscriptionOptions = [];
    this.oneTimeCard = null;
    this.subscriptionCard = null;
    this.subscriptionCards = [];
    this.frequencySelect = null;
    this.frequencySelects = [];
    this.frequencyInputs = [];
    this.frequencyContainer = null;
    this.frequencyContainers = [];
    this.oneTimePriceContainer = null;
    this.subscriptionPriceContainer = null;
    this.subscriptionPriceContainers = [];
    this.purchaseGroup = null;
    this.productForm = null;
    this.extraOffersContainer = null;
    this.extraOffersToggle = null;
    this.extraOfferCards = [];
    this.extraOffersExpanded = false;

    this.handleOneTimeChange = () => this.#onOptionChange('one_time');
    this.handleSubscriptionChange = () => this.#onOptionChange('subscription');
    this.handleFrequencyChange = (event) => this.#onFrequencyChange(event);
    this.handleFrequencyButtonChange = (event) => this.#onFrequencyButtonChange(event);
    this.handlePriceUpdate = () => this.#updateDisplayedPrices();
    this.handleVariantUpdate = (event) => this.#onVariantUpdate(event);
    this.handleExtraOffersToggle = () => this.#toggleExtraOffers();
    this.handleSubscriptionCardSurfaceClick = (event) => this.#onSubscriptionCardSurfaceClick(event);
  }

  connectedCallback() {
    this.#initDomReferences();
    this.#syncSubscriptionRadioGroupName();

    this.oneTimeOption?.addEventListener('change', this.handleOneTimeChange);
    for (const subscriptionOption of this.subscriptionOptions) {
      subscriptionOption.addEventListener('change', this.handleSubscriptionChange);
    }

    for (const frequencySelect of this.frequencySelects) {
      frequencySelect.addEventListener('change', this.handleFrequencyChange);
    }

    this.#addFrequencyButtonListeners();
    this.extraOffersToggle?.addEventListener('click', this.handleExtraOffersToggle);
    this.addEventListener('click', this.handleSubscriptionCardSurfaceClick);

    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);
    document.addEventListener(ThemeEvents.formUpdated, this.handlePriceUpdate);

    this.#initDefaultSelection();
    void this.#prefetchBonusVariantPrices();
  }

  disconnectedCallback() {
    this.oneTimeOption?.removeEventListener('change', this.handleOneTimeChange);
    for (const subscriptionOption of this.subscriptionOptions) {
      subscriptionOption.removeEventListener('change', this.handleSubscriptionChange);
    }

    for (const frequencySelect of this.frequencySelects) {
      frequencySelect.removeEventListener('change', this.handleFrequencyChange);
    }

    this.#removeFrequencyButtonListeners();
    this.extraOffersToggle?.removeEventListener('click', this.handleExtraOffersToggle);
    this.removeEventListener('click', this.handleSubscriptionCardSurfaceClick);

    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);
    document.removeEventListener(ThemeEvents.formUpdated, this.handlePriceUpdate);
  }

  #initDomReferences() {
    this.sellingPlanInput = this.querySelector('[data-ref="selling-plan-input"]');
    this.oneTimeOption = this.querySelector('[data-ref="one-time-option"]');
    this.subscriptionOptions = [...this.querySelectorAll('[data-ref="subscription-option"]')];
    this.subscriptionOption = this.subscriptionOptions[0] || null;
    this.oneTimeCard = this.querySelector('[data-ref="one-time-card"]');
    this.subscriptionCards = [...this.querySelectorAll('[data-ref="subscription-card"]')];
    this.subscriptionCard = this.subscriptionCards[0] || null;
    this.frequencySelects = [...this.querySelectorAll('[data-ref="frequency-select"]')];
    this.frequencySelect = this.frequencySelects[0] || null;
    this.frequencyInputs = [...this.querySelectorAll('[data-ref="frequency-input"]')];
    this.frequencyContainers = [...this.querySelectorAll('[data-ref="frequency-container"]')];
    this.frequencyContainer = this.frequencyContainers[0] || null;
    this.oneTimePriceContainer = this.querySelector('[data-ref="one-time-price"]');
    this.subscriptionPriceContainers = [...this.querySelectorAll('[data-ref="subscription-price"]')];
    this.subscriptionPriceContainer = this.subscriptionPriceContainers[0] || null;
    this.purchaseGroup = this.querySelector('[data-ref="purchase-group"]');
    this.extraOffersContainer = this.querySelector('[data-ref="subscription-v2-extra-offers"]');
    this.extraOffersToggle = this.querySelector('[data-ref="subscription-v2-toggle"]');
    this.extraOfferCards = this.#getExtraOfferItems();
    this.productForm =
      this.closest('[data-ref="product-section-content"]')?.querySelector('[data-ref="product-form"]') ||
      this.closest('[data-ref="popup-content"]')?.querySelector('[data-ref="product-form"]');

    this.#initExtraOffersToggle();
  }

  #getSubscriptionRootFromFetchedHtml(doc) {
    const blockKey = this.getAttribute('data-v2-block');
    if (blockKey) {
      const match = doc.querySelector(`subscription-v2-component[data-v2-block="${CSS.escape(blockKey)}"]`);
      if (match) return match;
    }

    const productForm = this.closest('[data-ref="product-form"]');
    const productId = productForm?.dataset?.productId;
    if (productId) {
      const form = doc.querySelector(`product-form[data-product-id="${CSS.escape(String(productId))}"]`);
      const inForm = form?.querySelector('subscription-v2-component');
      if (inForm) return inForm;
    }
    return doc.querySelector('subscription-v2-component');
  }

  #syncSubscriptionRadioGroupName() {
    const group = this.getAttribute('data-subscription-radio-group');
    if (!group) return;

    for (const input of this.querySelectorAll('input[type="radio"][name="subscription_type"]')) {
      input.name = group;
    }
  }

  #onSubscriptionCardSurfaceClick(event) {
    const card = event.target.closest('[data-ref="subscription-card"]');
    if (!card || !this.contains(card)) return;
    if (event.target.closest('[data-ref="frequency-container"]')) return;
    //if (event.target.closest('.subscription-v2__radio')) return;
    if (event.target.closest('a, button')) return;

    const radio = card.querySelector('[data-ref="subscription-option"]');
    if (!radio || radio.disabled || radio.checked) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onVariantUpdate(event) {
    if (!event.detail?.data?.html) return;

    const selectedOptionType = this.getSelectedOptionType();
    const selectedSubscriptionKey = this.#getSelectedSubscriptionKey();
    const selectedSellingPlan = this.#getSelectedFrequencyValue();
    const selectedSellingPlanName = this.#getSelectedSellingPlanName();
    const wasExtraOffersExpanded = this.extraOffersExpanded;
    const newSubscription = this.#getSubscriptionRootFromFetchedHtml(event.detail.data.html);
    if (!newSubscription) return;

    const hasSubscriptionContent = newSubscription.querySelector('[data-ref="selling-plan-input"]');

    if (!hasSubscriptionContent) {
      this.style.display = 'none';
      this.innerHTML = '';

      this.sellingPlanInput = null;
      this.oneTimeOption = null;
      this.subscriptionOption = null;
      this.subscriptionOptions = [];
      this.oneTimeCard = null;
      this.subscriptionCard = null;
      this.subscriptionCards = [];
      this.frequencySelect = null;
      this.frequencySelects = [];
      this.frequencyInputs = [];
      this.frequencyContainer = null;
      this.frequencyContainers = [];
      this.oneTimePriceContainer = null;
      this.subscriptionPriceContainer = null;
      this.subscriptionPriceContainers = [];
      this.extraOffersContainer = null;
      this.extraOffersToggle = null;
      this.extraOfferCards = [];
      this.extraOffersExpanded = false;

      this.dispatchEvent(new SubscriptionUpdatedEvent());
      return;
    }

    this.oneTimeOption?.removeEventListener('change', this.handleOneTimeChange);
    for (const subscriptionOption of this.subscriptionOptions) {
      subscriptionOption.removeEventListener('change', this.handleSubscriptionChange);
    }

    for (const frequencySelect of this.frequencySelects) {
      frequencySelect.removeEventListener('change', this.handleFrequencyChange);
    }

    this.#removeFrequencyButtonListeners();
    this.extraOffersToggle?.removeEventListener('click', this.handleExtraOffersToggle);

    this.style.display = '';
    this.innerHTML = newSubscription.innerHTML;

    this.#initDomReferences();
    this.#syncSubscriptionRadioGroupName();

    this.oneTimeOption?.addEventListener('change', this.handleOneTimeChange);
    for (const subscriptionOption of this.subscriptionOptions) {
      subscriptionOption.addEventListener('change', this.handleSubscriptionChange);
    }

    for (const frequencySelect of this.frequencySelects) {
      frequencySelect.addEventListener('change', this.handleFrequencyChange);
    }

    this.#addFrequencyButtonListeners();
    this.extraOffersToggle?.addEventListener('click', this.handleExtraOffersToggle);
    this.#restoreExtraOffersExpanded(wasExtraOffersExpanded);

    if (selectedOptionType === 'one_time' && this.oneTimeOption && this.dataset.requiresSellingPlan !== 'true') {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
      void this.#prefetchBonusVariantPrices();
      return;
    }

    if (selectedOptionType === 'subscription' && this.isSubscriptionActivated()) {
      const subscriptionOption = this.#getSubscriptionOptionByKey(selectedSubscriptionKey) || this.subscriptionOptions[0];

      if (subscriptionOption) {
        subscriptionOption.checked = true;
        this.#restoreSelectedFrequency(selectedSellingPlan, selectedSellingPlanName);
        this.#onOptionChange('subscription', true);
        void this.#prefetchBonusVariantPrices();
        return;
      }
    }

    this.#initDefaultSelection();
    void this.#prefetchBonusVariantPrices();
  }

  #initExtraOffersToggle() {
    this.extraOffersExpanded = false;

    if (!this.extraOffersContainer || !this.extraOffersToggle) return;

    const visibleOffers = parseInt(this.extraOffersContainer.dataset.visibleOffers, 10);
    const visibleOffersCount = Number.isNaN(visibleOffers) ? 0 : visibleOffers;
    const hasHiddenOffers = this.extraOfferCards.length > visibleOffersCount;

    for (const [index, offerCard] of this.extraOfferCards.entries()) {
      offerCard.hidden = index >= visibleOffersCount;
    }

    this.extraOffersToggle.classList.toggle('hidden', !hasHiddenOffers);
    this.extraOffersContainer.dataset.ready = 'true';
    this.extraOffersToggle.dataset.ready = 'true';

    if (!hasHiddenOffers) return;

    this.#updateExtraOffersToggleLabel();
  }

  #getExtraOfferItems() {
    if (!this.extraOffersContainer) return [];

    return [...this.extraOffersContainer.querySelectorAll('[data-ref="subscription-v2-extra-offer-item"]')];
  }

  #toggleExtraOffers() {
    if (!this.extraOffersContainer || !this.extraOffersToggle || this.extraOfferCards.length === 0) return;

    this.extraOffersExpanded = !this.extraOffersExpanded;
    const visibleOffers = parseInt(this.extraOffersContainer.dataset.visibleOffers, 10);
    const visibleOffersCount = Number.isNaN(visibleOffers) ? 0 : visibleOffers;

    for (const [index, offerCard] of this.extraOfferCards.entries()) {
      offerCard.hidden = !this.extraOffersExpanded && index >= visibleOffersCount;
    }

    this.#updateExtraOffersToggleLabel();
  }

  #updateExtraOffersToggleLabel() {
    if (!this.extraOffersToggle) return;

    const showMoreText = this.extraOffersToggle.dataset.showMoreText || '';
    const showLessText = this.extraOffersToggle.dataset.showLessText || '';
    this.extraOffersToggle.textContent = this.extraOffersExpanded ? showLessText : showMoreText;
  }

  #restoreExtraOffersExpanded(wasExpanded) {
    if (!wasExpanded || !this.extraOffersContainer || !this.extraOffersToggle) return;

    this.extraOffersExpanded = true;
    for (const offerCard of this.extraOfferCards) {
      offerCard.hidden = false;
    }

    this.#updateExtraOffersToggleLabel();
  }

  #initDefaultSelection() {
    const requiresSellingPlan = this.dataset.requiresSellingPlan === 'true';
    const firstSubscriptionOption = this.subscriptionOptions[0] || null;

    if (requiresSellingPlan) {
      if (firstSubscriptionOption && this.isSubscriptionActivated()) {
        firstSubscriptionOption.checked = true;
        this.#onOptionChange('subscription', true);
      }
      return;
    }

    if (!this.oneTimeOption) return;

    if (this.#isQuantityBreaksVariantPickerActive()) {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
      return;
    }

    const defaultSelected = this.dataset.defaultSelected || 'one_time';

    if (defaultSelected === 'subscription' && firstSubscriptionOption && this.isSubscriptionActivated()) {
      firstSubscriptionOption.checked = true;
      this.#onOptionChange('subscription', true);
    } else {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
    }
  }

  #isQuantityBreaksVariantPickerActive() {
    const quantityBreaks = this.productForm?.querySelector('[data-ref="quantity-breaks"]');
    return quantityBreaks?.dataset.showVariantPicker === 'true';
  }

  #onOptionChange(optionType, notify = true) {
    this.#updateSelectedValueText(optionType);
    this.#updateSelectedCard(optionType);
    this.#updatePurchaseGroupVisibility(optionType);
    this.#updateStickySelectedValue(optionType);

    if (optionType === 'subscription') {
      const selectedFrequencyValue = this.getSelectedSellingPlan() || this.#getSelectedFrequencyValue() || this.#getFirstFrequencyValue();
      this.#syncFrequencyInputs(selectedFrequencyValue);
      this.#showFrequencySelect();
      this.#updateSellingPlanInput(this.#getSelectedFrequencyValue());
    } else {
      this.#hideFrequencySelect();
      this.#updateSellingPlanInput('');
    }

    this.#updateDisplayedPrices();
    this.#updateStickyPriceHtml();
    if (notify) {
      this.dispatchEvent(new SubscriptionUpdatedEvent());
    }
  }

  #updatePurchaseGroupVisibility(optionType) {
    if (!this.purchaseGroup) return;
    const shouldHide = optionType === 'subscription';
    this.purchaseGroup.classList.toggle('hidden', shouldHide);
  }

  #onFrequencyChange(event) {
    const selectedValue = event?.target?.value || this.#getSelectedFrequencyValue();
    this.#syncFrequencyInputs(selectedValue);
    this.#updateSellingPlanInput(selectedValue);
    this.#updateDisplayedPrices();
    this.#updateStickySelectedValue('subscription');
    this.dispatchEvent(new SubscriptionUpdatedEvent());
  }

  #updateDisplayedPrices() {
    const { priceAfterQB, priceBeforeQB, compareAtPrice, quantity, qbDiscount, crossSellPrice, crossSellCompareAtPrice } = this.#getBasePrices();
    if (priceAfterQB === null || priceAfterQB === undefined) return;

    const oneTimeTotal = priceAfterQB + crossSellPrice;
    const oneTimeCompareAt = compareAtPrice + crossSellCompareAtPrice;
    this.#updatePriceElement(this.oneTimePriceContainer, oneTimeTotal, oneTimeCompareAt, false);

    let subscriptionPrice = this.#calculateSubscriptionPrice(priceBeforeQB, quantity);

    if (qbDiscount) {
      if (qbDiscount.from === 'quantity-selector') {
        if (quantity >= qbDiscount.breakpoint) {
          let qbDiscountTotalValue = qbDiscount.value * Math.floor(quantity / qbDiscount.step);
          subscriptionPrice = subscriptionPrice - qbDiscountTotalValue;
        }
      } else if (qbDiscount.from === 'quantity-break-item') {
        if (qbDiscount.type === 'percentage' && qbDiscount.percentage > 0) {
          subscriptionPrice = Math.floor((subscriptionPrice * (100 - qbDiscount.percentage)) / 100);
        } else if (qbDiscount.value > 0) {
          subscriptionPrice = subscriptionPrice - qbDiscount.value;
        }
      }
    }

    const selectedFrequencyBucket = this.#getSelectedFrequencyBucket();
    const selectedPlanAdjustment = this.#getSelectedPlanAdjustment();

    for (const subscriptionPriceContainer of this.subscriptionPriceContainers) {
      const subscriptionCard = subscriptionPriceContainer.closest('[data-ref="subscription-card"]');
      const { price: bonusPrice, compareAtPrice: bonusCompareAtPrice } = this.#getBonusPriceTotals(subscriptionCard, selectedFrequencyBucket, selectedPlanAdjustment);
      const subscriptionTotal = Math.max(0, subscriptionPrice) + crossSellPrice + bonusPrice;
      const subscriptionCompareAt = compareAtPrice + crossSellCompareAtPrice + bonusCompareAtPrice;

      this.#updatePriceElement(subscriptionPriceContainer, subscriptionTotal, subscriptionCompareAt, true);
    }

    this.#updateStickyPriceHtml();
  }

  #getBasePrices() {
    const quantityBreaks = this.productForm?.querySelector('[data-ref="quantity-breaks"]');

    if (quantityBreaks && typeof quantityBreaks.getPrices === 'function') {
      const prices = quantityBreaks.getPrices();
      const quantity = parseInt(quantityBreaks.getSelectedQuantity?.()?.dataset?.quantity) || 1;
      const discount = typeof quantityBreaks.getDiscount === 'function' ? quantityBreaks.getDiscount() : null;

      const priceAfterQB = parseInt(prices.price) || 0;
      const compareAtPrice = parseInt(prices.compareAtPrice) || priceAfterQB;

      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;
      if (typeof quantityBreaks.getCrossSellPrices === 'function') {
        const crossSellPrices = quantityBreaks.getCrossSellPrices();
        crossSellPrice = parseInt(crossSellPrices.price) || 0;
        crossSellCompareAtPrice = parseInt(crossSellPrices.compareAtPrice) || crossSellPrice;
      }

      const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
      const variantUnitPrice = parseInt(addToCartContainer?.dataset.price) || 0;
      const variantUnitCompareAtPrice = parseInt(addToCartContainer?.dataset.compareAtPrice) || variantUnitPrice;

      const priceBeforeQB = variantUnitPrice * quantity;

      return {
        priceAfterQB,
        priceBeforeQB,
        compareAtPrice: variantUnitCompareAtPrice * quantity,
        quantity,
        qbDiscount: discount,
        crossSellPrice,
        crossSellCompareAtPrice,
      };
    }

    const quantityContainer = this.productForm?.querySelector('[data-ref="product-quantity-selector"]');
    const quantitySelector = quantityContainer?.querySelector('quantity-selector');
    const quantityValue = parseInt(quantityContainer?.querySelector('input[name="quantity"]')?.value) || 1;
    const discount = typeof quantitySelector.getDiscount === 'function' ? quantitySelector.getDiscount() : null;
    const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
    const variantUnitPrice = parseInt(addToCartContainer?.dataset.price) || 0;
    const variantUnitCompareAtPrice = parseInt(addToCartContainer?.dataset.compareAtPrice) || variantUnitPrice;

    const priceBeforeQB = variantUnitPrice * quantityValue;
    const variantCompareAtPrice = variantUnitCompareAtPrice * quantityValue;
    let priceAfterQB = priceBeforeQB;

    if (discount !== null && quantityValue >= discount.breakpoint) {
      let quantityBreaksDiscountTotalValue = discount.value * Math.floor(quantityValue / discount.step);
      priceAfterQB = priceBeforeQB - quantityBreaksDiscountTotalValue;
    }

    return {
      priceAfterQB,
      priceBeforeQB,
      compareAtPrice: variantCompareAtPrice,
      quantity: quantityValue,
      qbDiscount: discount,
      crossSellPrice: 0,
      crossSellCompareAtPrice: 0,
    };
  }

  #calculateSubscriptionPrice(basePrice, quantity = 1) {
    const adjustment = this.#getSelectedPlanAdjustment();
    return this.#applyPriceAdjustment(basePrice, adjustment, quantity);
  }

  #getSelectedPlanAdjustment() {
    const selectedElement = this.#getSelectedFrequencyElement();
    if (!selectedElement) return null;

    return {
      type: selectedElement.dataset.adjustmentType || 'percentage',
      value: parseFloat(selectedElement.dataset.adjustmentValue) || 0,
    };
  }

  #applyPriceAdjustment(basePrice, adjustment, quantity = 1) {
    if (!adjustment) {
      return basePrice;
    }

    switch (adjustment.type) {
      case 'percentage': {
        let unitBasePrice = basePrice / quantity;
        let unitDiscountAmount = unitBasePrice * adjustment.value / 100;
        let unitSubscriptionPrice = unitBasePrice - unitDiscountAmount;
        unitSubscriptionPrice = Math.round(unitSubscriptionPrice);
        return unitSubscriptionPrice * quantity;
      }
      case 'fixed_amount':
        return Math.max(0, basePrice - adjustment.value * quantity);
      case 'price':
        return adjustment.value * quantity;
      default:
        return basePrice;
    }
  }

  #updatePriceElement(container, price, compareAtPrice, showSubscriptionBadge = false) {
    if (!container) return;

    const priceEl = container.querySelector('[data-ref="price"]');
    const compareAtPriceEl = container.querySelector('[data-ref="compare-at-price"]');
    const badgeEl = container.querySelector('[data-ref="difference-price-value"]');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.dataset.value = price;
    }

    const hasSavings = compareAtPrice > price;
    container.querySelector('.prices')?.classList.toggle('prices--with-sale', hasSavings);

    if (compareAtPriceEl) {
      if (hasSavings) {
        compareAtPriceEl.textContent = formatMoney(compareAtPrice);
        compareAtPriceEl.dataset.value = compareAtPrice;
      } else {
        compareAtPriceEl.textContent = '';
        compareAtPriceEl.dataset.value = '';
      }
    }

    if (badgeEl) {
      badgeEl.textContent = showSubscriptionBadge ? this.#formatSubscriptionDiscount(compareAtPrice, price) : '';
    }
  }

  /**
   * Pourcentage affiché sur le badge : dérivé des montants réellement affichés (compare vs prix),
   * pas du pourcentage admin du plan — sinon écart avec l’arrondi centimes (Math.round sur l’unité).
   */
  #formatSubscriptionDiscount(compareAtPrice, price) {
    const adjustment = this.#getSelectedPlanAdjustment();
    if (!adjustment || adjustment.value === 0) return '';

    switch (adjustment.type) {
      case 'percentage': {
        if (!compareAtPrice || compareAtPrice <= price) return '';
        const pct = ((compareAtPrice - price) / compareAtPrice) * 100;
        return `${pct.toFixed(1).replace('.', ',')}%`;
      }
      case 'fixed_amount':
        return formatMoney(adjustment.value);
      case 'price':
        return formatMoney(adjustment.value);
      default:
        return '';
    }
  }

  #updateSelectedValueText(optionType) {
    this.querySelector('.subscription-v2__selected-value--onetime')?.classList.remove('hidden');
    this.querySelector('.subscription-v2__selected-value--subscription')?.classList.remove('hidden');

    if (optionType === 'one_time') {
      this.querySelector('.subscription-v2__selected-value--subscription')?.classList.add('hidden');
    } else {
      this.querySelector('.subscription-v2__selected-value--onetime')?.classList.add('hidden');
    }
  }

  #updateSelectedCard(optionType) {
    this.oneTimeCard?.classList.remove('subscription-v2__option--selected');
    for (const subscriptionCard of this.subscriptionCards) {
      subscriptionCard.classList.remove('subscription-v2__option--selected');
    }

    if (optionType === 'one_time') {
      this.oneTimeCard?.classList.add('subscription-v2__option--selected');
    } else {
      this.#getSelectedSubscriptionCard()?.classList.add('subscription-v2__option--selected');
      this.#ensureSelectedSubscriptionCardVisible();
    }
  }

  #ensureSelectedSubscriptionCardVisible() {
    const selectedSubscriptionCard = this.#getSelectedSubscriptionCard();
    if (!selectedSubscriptionCard) return;

    const selectedOfferWrapper = this.extraOfferCards.find((offerCard) => offerCard.contains(selectedSubscriptionCard));
    if (!selectedOfferWrapper || !selectedOfferWrapper.hidden) return;

    this.extraOffersExpanded = true;
    for (const offerCard of this.extraOfferCards) {
      offerCard.hidden = false;
    }
    this.#updateExtraOffersToggleLabel();
  }

  getSelectedOptionType() {
    if (this.subscriptionOptions.some((option) => option.checked)) return 'subscription';
    return 'one_time';
  }

  #getSelectedSubscriptionKey() {
    const selectedSubscriptionOption = this.subscriptionOptions.find((option) => option.checked);
    return selectedSubscriptionOption?.id || '';
  }

  #getSubscriptionOptionByKey(key) {
    if (!key) return null;
    return this.subscriptionOptions.find((option) => option.id === key) || null;
  }

  #showFrequencySelect() {
    for (const frequencyContainer of this.frequencyContainers) {
      frequencyContainer.style.display = 'none';
    }

    const selectedFrequencyContainer = this.#getSelectedSubscriptionCard()?.querySelector('[data-ref="frequency-container"]');
    if (selectedFrequencyContainer) {
      selectedFrequencyContainer.style.display = 'flex';
    }
  }

  #hideFrequencySelect() {
    for (const frequencyContainer of this.frequencyContainers) {
      frequencyContainer.style.display = 'none';
    }
  }

  #addFrequencyButtonListeners() {
    if (!this.frequencyInputs) return;

    for (const input of this.frequencyInputs) {
      input.addEventListener('change', this.handleFrequencyButtonChange);
    }
  }

  #removeFrequencyButtonListeners() {
    if (!this.frequencyInputs) return;

    for (const input of this.frequencyInputs) {
      input.removeEventListener('change', this.handleFrequencyButtonChange);
    }
  }

  #onFrequencyButtonChange(event) {
    const selectedValue = event?.target?.value || this.#getSelectedFrequencyValue();
    this.#syncFrequencyInputs(selectedValue);
    this.#updateSellingPlanInput(selectedValue);
    this.#updateDisplayedPrices();
    this.#updateStickySelectedValue('subscription');
    this.dispatchEvent(new SubscriptionUpdatedEvent());
  }

  #isHostRenderable(host = this) {
    if (!host) return false;

    let node = host;
    const stopAt = this.productForm || document.body;

    while (node && node !== stopAt) {
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement;
    }

    return true;
  }

  #isPrimarySubscriptionHost() {
    const productForm = this.productForm;
    if (!productForm) return this.#isHostRenderable();

    const hosts = productForm.querySelectorAll('subscription-component, subscription-v2-component');
    for (const host of hosts) {
      if (this.#isHostRenderable(host)) return host === this;
    }

    return false;
  }

  #updateStickySelectedValue(optionType) {
    if (!this.#isPrimarySubscriptionHost()) return;

    const addToCartContainers = document.querySelectorAll('[data-ref="add-to-cart-button-container"]');
    if (addToCartContainers.length === 0) return;

    const selectedValueElement =
      optionType === 'subscription'
        ? this.querySelector('.subscription-v2__selected-value--subscription')
        : this.querySelector('.subscription-v2__selected-value--onetime');
    let selectedValueText = selectedValueElement?.textContent?.trim() || '';

    if (optionType === 'subscription') {
      const frequencyLabel = this.#getSelectedFrequencyLabel();
      if (frequencyLabel) {
        selectedValueText = `${selectedValueText} ${frequencyLabel}`.trim();
      }
    }

    addToCartContainers.forEach((container) => {
      const stickyValue = container.querySelector('[data-ref="sticky-selected-value"]');
      if (stickyValue) stickyValue.textContent = selectedValueText;
    });
  }

  #updateStickyPriceHtml() {
    if (!this.#isPrimarySubscriptionHost()) return;

    const addToCartContainers = document.querySelectorAll('[data-ref="add-to-cart-button-container"]');
    if (addToCartContainers.length === 0) return;

    const optionType = this.getSelectedOptionType();
    const priceSource =
      optionType === 'subscription'
        ? this.#getSelectedSubscriptionCard()?.querySelector('[data-ref="subscription-price"]')
        : this.oneTimePriceContainer;
    if (!priceSource) return;

    let priceHtml = '';
    if (priceSource) {
      const priceClone = document.createElement('div');
      priceClone.innerHTML = priceSource.innerHTML.trim();
      priceClone.querySelector('.badge')?.remove();
      for (const refEl of priceClone.querySelectorAll('[data-ref="price"], [data-ref="compare-at-price"], [data-ref="difference-price-value"]')) {
        refEl.removeAttribute('data-ref');
      }
      priceHtml = priceClone.innerHTML.trim();
    }

    addToCartContainers.forEach((container) => {
      const stickyPrice = container.querySelector('[data-ref="sticky-price"]');
      if (stickyPrice) stickyPrice.innerHTML = priceHtml;
    });
  }

  #getSelectedFrequencyValue() {
    const selectedSubscriptionCard = this.#getSelectedSubscriptionCard();
    const selectedFrequencySelect = selectedSubscriptionCard?.querySelector('[data-ref="frequency-select"]');
    if (selectedFrequencySelect) {
      return selectedFrequencySelect.value || '';
    }

    if (selectedSubscriptionCard) {
      const checkedInput = selectedSubscriptionCard.querySelector('[data-ref="frequency-input"]:checked');
      return checkedInput?.value || '';
    }

    return '';
  }

  #getSelectedFrequencyLabel() {
    const selectedSubscriptionCard = this.#getSelectedSubscriptionCard();
    const selectedFrequencySelect = selectedSubscriptionCard?.querySelector('[data-ref="frequency-select"]');
    if (selectedFrequencySelect) {
      return selectedFrequencySelect.options[selectedFrequencySelect.selectedIndex]?.textContent?.trim() || '';
    }

    const checkedInput = selectedSubscriptionCard?.querySelector('[data-ref="frequency-input"]:checked');
    if (!checkedInput) return '';
    const label = selectedSubscriptionCard.querySelector(`label[for="${checkedInput.id}"]`);
    return label?.textContent?.trim() || '';
  }

  #getSelectedFrequencyElement() {
    const selectedSubscriptionCard = this.#getSelectedSubscriptionCard();
    const selectedFrequencySelect = selectedSubscriptionCard?.querySelector('[data-ref="frequency-select"]');
    if (selectedFrequencySelect) {
      return selectedFrequencySelect.options[selectedFrequencySelect.selectedIndex] || null;
    }

    if (selectedSubscriptionCard) {
      return selectedSubscriptionCard.querySelector('[data-ref="frequency-input"]:checked');
    }

    return null;
  }

  #getSelectedSellingPlanName() {
    const selectedFrequencyElement = this.#getSelectedFrequencyElement();
    return selectedFrequencyElement?.dataset.sellingPlanName || this.#getSelectedFrequencyLabel() || '';
  }

  #restoreSelectedFrequency(selectedValue, selectedName) {
    const restoredValue =
      this.#getAvailableFrequencyValue(selectedValue) ||
      this.#getFrequencyValueByName(selectedName) ||
      this.#getFirstFrequencyValue();

    this.#syncFrequencyInputs(restoredValue);
  }

  #getAvailableFrequencyValue(value) {
    if (!value) return '';

    for (const frequencySelect of this.frequencySelects) {
      const hasMatchingOption = [...frequencySelect.options].some((option) => option.value === value);
      if (hasMatchingOption) return value;
    }

    return this.frequencyInputs.some((input) => input.value === value) ? value : '';
  }

  #getFrequencyValueByName(name) {
    const normalizedName = this.#normalizeFrequencyName(name);
    if (!normalizedName) return '';

    for (const frequencySelect of this.frequencySelects) {
      const matchingOption = [...frequencySelect.options].find((option) => this.#normalizeFrequencyName(option.dataset.sellingPlanName || option.textContent) === normalizedName);
      if (matchingOption) return matchingOption.value;
    }

    const matchingInput = this.frequencyInputs.find((input) => this.#normalizeFrequencyName(input.dataset.sellingPlanName || this.#getFrequencyInputLabel(input)) === normalizedName);
    return matchingInput?.value || '';
  }

  #getFrequencyInputLabel(input) {
    if (!input?.id) return '';
    return this.querySelector(`label[for="${CSS.escape(input.id)}"]`)?.textContent || '';
  }

  #normalizeFrequencyName(name) {
    return String(name || '').replace(/\s+/g, ' ').trim();
  }

  #getSelectedFrequencyBucket() {
    const normalizedPlanName = String(this.#getSelectedSellingPlanName() || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    const monthMatch = normalizedPlanName.match(/(\d+)\s*(mois|month|months)/);
    if (!monthMatch) return '';

    switch (monthMatch[1]) {
      case '1':
        return '1_month';
      case '2':
        return '2_month';
      case '3':
        return '3_month';
      default:
        return '';
    }
  }

  #getFirstFrequencyValue() {
    const firstFrequencySelect = this.frequencySelects[0];
    if (firstFrequencySelect) {
      return firstFrequencySelect.value || '';
    }

    return this.frequencyInputs[0]?.value || '';
  }

  #syncFrequencyInputs(value) {
    if (!value) return;

    for (const frequencySelect of this.frequencySelects) {
      const hasMatchingOption = [...frequencySelect.options].some((option) => option.value === value);
      if (hasMatchingOption) {
        frequencySelect.value = value;
      }
    }

    for (const frequencyInput of this.frequencyInputs) {
      frequencyInput.checked = frequencyInput.value === value;
    }
  }

  #getSelectedSubscriptionCard() {
    const selectedSubscriptionOption = this.subscriptionOptions.find((option) => option.checked) || this.subscriptionOptions[0];
    return selectedSubscriptionOption?.closest('[data-ref="subscription-card"]') || null;
  }

  #getCardBonusProducts(card) {
    const bonusProductsData = card?.dataset?.bonusProducts;
    if (!bonusProductsData) return [];

    try {
      const parsedBonusProducts = JSON.parse(bonusProductsData);
      return Array.isArray(parsedBonusProducts) ? parsedBonusProducts : [];
    } catch {
      return [];
    }
  }

  async #prefetchBonusVariantPrices() {
    const bonusVariantIds = [...new Set(
      this.subscriptionCards.flatMap((subscriptionCard) => {
        return this.#getCardBonusProducts(subscriptionCard)
          .map((bonusProduct) => bonusProduct?.variant_id ? String(bonusProduct.variant_id) : '')
          .filter(Boolean);
      }),
    )];

    if (bonusVariantIds.length === 0) return;

    await Promise.all(bonusVariantIds.map((variantId) => this.#fetchBonusVariantPriceData(variantId)));
    this.#updateDisplayedPrices();
    this.dispatchEvent(new SubscriptionUpdatedEvent());
  }

  async #fetchBonusVariantPriceData(variantId) {
    const cacheKey = String(variantId);
    const cachedValue = bonusVariantPriceCache.get(cacheKey);

    if (cachedValue) {
      return cachedValue instanceof Promise ? cachedValue : Promise.resolve(cachedValue);
    }

    const request = fetch(`${window.Shopify.routes.root}variants/${encodeURIComponent(cacheKey)}.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load variant ${cacheKey}`);
        }

        return response.json();
      })
      .then((variant) => {
        const variantPriceData = {
          price: parseInt(variant.price, 10) || 0,
          compareAtPrice: parseInt(variant.compare_at_price, 10) || parseInt(variant.price, 10) || 0,
          sellingPlans: this.#getSellingPlanPriceMap(variant.selling_plan_allocations),
        };

        const productUrl = this.#getBonusProductUrlFromVariant(variant);
        if (!productUrl || Object.keys(variantPriceData.sellingPlans).length > 0) {
          bonusVariantPriceCache.set(cacheKey, variantPriceData);
          return variantPriceData;
        }

        return this.#enrichBonusVariantPriceDataFromProductJson(cacheKey, productUrl, variantPriceData);
      })
      .catch((error) => {
        bonusVariantPriceCache.delete(cacheKey);
        console.error('Failed to load bonus variant price:', error);
        return null;
      });

    bonusVariantPriceCache.set(cacheKey, request);
    return request;
  }

  #getSellingPlanPriceMap(sellingPlanAllocations) {
    if (!Array.isArray(sellingPlanAllocations) || sellingPlanAllocations.length === 0) {
      return {};
    }

    return sellingPlanAllocations.reduce((sellingPlans, allocation) => {
      const sellingPlanId = allocation?.selling_plan_id ? String(allocation.selling_plan_id) : '';
      if (!sellingPlanId) return sellingPlans;

      sellingPlans[sellingPlanId] = {
        price: parseInt(allocation.price, 10) || 0,
        compareAtPrice: parseInt(allocation.compare_at_price, 10) || parseInt(allocation.price, 10) || 0,
      };

      return sellingPlans;
    }, {});
  }

  #getBonusProductUrlFromVariant(variant) {
    const variantUrl = variant?.url || variant?.product_url || variant?.productUrl || '';
    if (!variantUrl) return '';

    try {
      const url = new URL(variantUrl, window.location.origin);
      url.search = '';
      url.hash = '';
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      return url.toString();
    } catch {
      return '';
    }
  }

  async #enrichBonusVariantPriceDataFromProductJson(cacheKey, productUrl, variantPriceData) {
    try {
      const response = await fetch(productUrl);
      if (!response.ok) {
        bonusVariantPriceCache.set(cacheKey, variantPriceData);
        return variantPriceData;
      }

      const product = await response.json();
      const matchingVariant = product?.variants?.find((variant) => String(variant.id) === cacheKey);
      if (matchingVariant) {
        variantPriceData.sellingPlans = this.#getSellingPlanPriceMap(matchingVariant.selling_plan_allocations);
        variantPriceData.compareAtPrice = parseInt(matchingVariant.compare_at_price, 10) || variantPriceData.compareAtPrice;
      }

      bonusVariantPriceCache.set(cacheKey, variantPriceData);
      return variantPriceData;
    } catch {
      bonusVariantPriceCache.set(cacheKey, variantPriceData);
      return variantPriceData;
    }
  }

  #getBonusPriceTotals(card, selectedFrequencyBucket, selectedPlanAdjustment) {
    const bonusProducts = this.#getCardBonusProducts(card);
    if (bonusProducts.length === 0) {
      return { price: 0, compareAtPrice: 0 };
    }

    return bonusProducts.reduce((totals, bonusProduct) => {
      const variantId = bonusProduct?.variant_id ? String(bonusProduct.variant_id) : '';
      if (!variantId) return totals;

      const variantPriceData = bonusVariantPriceCache.get(variantId);
      if (!variantPriceData || variantPriceData instanceof Promise) return totals;

      const sellingPlanId = this.#getMatchingBonusSellingPlanId(bonusProduct, selectedFrequencyBucket);
      if (selectedFrequencyBucket && !sellingPlanId) return totals;

      const exactSellingPlanPrice = sellingPlanId ? variantPriceData.sellingPlans?.[String(sellingPlanId)] : null;
      const computedPrice = exactSellingPlanPrice?.price ?? this.#getBonusSubscriptionPrice(variantPriceData.price, selectedPlanAdjustment);
      const computedCompareAtPrice = exactSellingPlanPrice?.compareAtPrice ?? variantPriceData.compareAtPrice;

      return {
        price: totals.price + computedPrice,
        compareAtPrice: totals.compareAtPrice + computedCompareAtPrice,
      };
    }, { price: 0, compareAtPrice: 0 });
  }

  #getBonusSubscriptionPrice(basePrice, selectedPlanAdjustment) {
    if (!selectedPlanAdjustment) return basePrice;

    if (selectedPlanAdjustment.type === 'price') {
      return basePrice;
    }

    return this.#applyPriceAdjustment(basePrice, selectedPlanAdjustment, 1);
  }

  #getSelectedBonusProducts() {
    if (!this.isSubscriptionSelected()) return [];

    const bonusProducts = this.#getCardBonusProducts(this.#getSelectedSubscriptionCard());
    if (bonusProducts.length === 0) return [];

    const selectedFrequencyBucket = this.#getSelectedFrequencyBucket();

    return bonusProducts
      .map((bonusProduct) => {
        if (!bonusProduct?.variant_id) return null;

        const sellingPlanId = this.#getMatchingBonusSellingPlanId(bonusProduct, selectedFrequencyBucket);
        if (selectedFrequencyBucket && !sellingPlanId) return null;

        return {
          variantId: String(bonusProduct.variant_id),
          quantity: 1,
          sellingPlanId,
        };
      })
      .filter(Boolean);
  }

  getSelectedBonusItems() {
    return this.#getSelectedBonusProducts();
  }

  #getMatchingBonusSellingPlanId(bonusProduct, selectedFrequencyBucket) {
    if (selectedFrequencyBucket === '1_month') {
      return bonusProduct.selling_plan_1_month ? String(bonusProduct.selling_plan_1_month) : '';
    }

    if (selectedFrequencyBucket === '2_month') {
      return bonusProduct.selling_plan_2_month ? String(bonusProduct.selling_plan_2_month) : '';
    }

    if (selectedFrequencyBucket === '3_month') {
      return bonusProduct.selling_plan_3_month ? String(bonusProduct.selling_plan_3_month) : '';
    }

    return '';
  }


  #updateSellingPlanInput(value) {
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = value;
    }
  }

  getSelectedSellingPlan() {
    return this.sellingPlanInput?.value || '';
  }

  isSubscriptionSelected() {
    return this.subscriptionOptions.some((option) => option.checked);
  }

  isSubscriptionActivated() {
    return this.#isHostRenderable();
  }

  getPriceAdjustment() {
    if (!this.isSubscriptionSelected()) return null;
    return this.#getSelectedPlanAdjustment();
  }

  getSelectedBonusPriceTotals() {
    if (!this.isSubscriptionSelected()) {
      return { price: 0, compare_at_price: 0 };
    }

    const selectedFrequencyBucket = this.#getSelectedFrequencyBucket();
    const selectedPlanAdjustment = this.#getSelectedPlanAdjustment();
    const totals = this.#getBonusPriceTotals(this.#getSelectedSubscriptionCard(), selectedFrequencyBucket, selectedPlanAdjustment);

    return {
      price: totals.price,
      compare_at_price: totals.compareAtPrice,
    };
  }

  calculateAdjustedPrice(basePrice, quantity = 1) {
    return this.#applyPriceAdjustment(basePrice, this.getPriceAdjustment(), quantity);
  }
}

customElements.define('subscription-v2-component', SubscriptionV2Component);
